// Controller Stimulus pour gérer l'affichage/masquage des champs de TVA manuelle
// et le calcul en temps réel du total de TVA avec validation
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'checkbox',
    'autoMessage',
    'manualFields',
    'vatField',
    'totalDisplay',
    'totalDisplay2',
    'htInput',
    'htDisplay',
    'ttcAmount',
    'warningBox',
    'warningMessage',
    'rgWarning',
    'helpPanel',
    'suggestion55',
    'suggestion10',
    'suggestion20',
    'suggestionText55',
    'suggestionText10',
    'suggestionText20',
    'vatInput55',
    'vatInput10',
    'vatInput20',
  ];

  static values = {
    retentionType: String,
    contractTotalHt: Number,
    baseHt55: Number,
    baseHt10: Number,
    baseHt20: Number,
    isAdvance: Boolean,
  };

  connect() {
    // Initialiser l'affichage selon l'état de la checkbox.
    // The Phase 6 refactor removed the DERIVED-mode early-return: the WPS
    // TVA card is now hidden whenever invoices are attached, so this
    // controller only ever instantiates on the no-invoices path.
    this.toggle();
    // Calculer les suggestions au chargement si mode manuel actif
    if (this.checkboxTarget.checked) {
      this.calculateSuggestions();
    }
  }

  toggle() {
    const isManual = this.checkboxTarget.checked;

    // Sync the canonical hidden field. The visible checkbox is name-less
    // (UI trigger only — see _form.html.erb:357 / 339) so the form would
    // otherwise always submit the server-rendered initial value and drop
    // the user's choice. Regression introduced by 8be9072f.
    const hiddenField = this.element.querySelector(
      'input[type="hidden"][name="work_progress_statement[manual_vat_entry]"]'
    );
    if (hiddenField) hiddenField.value = isManual ? '1' : '0';

    if (isManual) {
      // Afficher les champs manuels, masquer le message auto
      this.autoMessageTarget.classList.add('hidden');
      this.manualFieldsTarget.classList.remove('hidden');
      this.calculateTotal();
      this.showRgWarning();
      this.calculateSuggestions();
    } else {
      // Masquer les champs manuels, afficher le message auto
      this.autoMessageTarget.classList.remove('hidden');
      this.manualFieldsTarget.classList.add('hidden');
      // Masquer aussi le warning s'il est visible
      if (this.hasWarningBoxTarget) {
        this.warningBoxTarget.classList.add('hidden');
      }
      // Masquer le warning RG si présent
      if (this.hasRgWarningTarget) {
        this.rgWarningTarget.classList.add('hidden');
      }
      // Masquer les suggestions
      this.hideSuggestions();
    }
  }

  showRgWarning() {
    // Afficher le warning RG et le panneau d'aide seulement si :
    // - Mode manuel activé
    // - RG sur HT
    // - Pas un acompte
    const shouldShow = this.retentionTypeValue === 'rg_ht' && !this.isAdvanceValue;

    if (this.hasRgWarningTarget) {
      if (shouldShow) {
        this.rgWarningTarget.classList.remove('hidden');
      } else {
        this.rgWarningTarget.classList.add('hidden');
      }
    }

    if (this.hasHelpPanelTarget) {
      if (shouldShow) {
        this.helpPanelTarget.classList.remove('hidden');
      } else {
        this.helpPanelTarget.classList.add('hidden');
      }
    }
  }

  calculateSuggestions() {
    // Ne calculer que si mode manuel + RG sur HT + pas acompte
    if (
      !this.checkboxTarget.checked ||
      this.retentionTypeValue !== 'rg_ht' ||
      this.isAdvanceValue
    ) {
      this.hideSuggestions();
      return;
    }

    // Calculer et afficher les suggestions pour chaque taux actif
    this.calculateSuggestionForRate('55', 5.5, this.baseHt55Value);
    this.calculateSuggestionForRate('10', 10, this.baseHt10Value);
    this.calculateSuggestionForRate('20', 20, this.baseHt20Value);
  }

  calculateSuggestionForRate(rateKey, ratePercent, baseHt) {
    // Si baseHt est 0, ce taux n'est pas actif
    if (baseHt <= 0) {
      return;
    }

    // Chercher les éléments dans le DOM plutôt que via les targets
    // Car le composant CurrencyInput peut les placer différemment
    const fieldName = `vat_amount_${rateKey.replace('55', '5_5')}`;
    const vatInput = this.element.querySelector(
      `input[name="work_progress_statement[${fieldName}]"]`
    );

    const suggestionDiv = this.element.querySelector(
      `[data-vat-toggle-target="suggestion${rateKey}"]`
    );
    const suggestionText = this.element.querySelector(
      `[data-vat-toggle-target="suggestionText${rateKey}"]`
    );

    // Vérifier que tous les éléments existent
    if (!vatInput || !suggestionDiv || !suggestionText) {
      return;
    }

    // Récupérer le montant de TVA saisi par l'utilisateur
    const vatInputAmount = this.parseAmount(vatInput.value);

    // Si aucun montant saisi, masquer la suggestion
    if (vatInputAmount <= 0) {
      suggestionDiv.classList.add('hidden');
      return;
    }

    // CALCUL INVERSE : partir du montant de TVA pour retrouver la base HT
    // Formule : Base HT = Montant TVA ÷ Taux TVA
    const baseHtFromVat = Math.round((vatInputAmount / (ratePercent / 100)) * 100) / 100;

    // Appliquer la RG 5% sur cette base HT
    const htAfterRg = Math.round(baseHtFromVat * 0.95 * 100) / 100;

    // Calculer la TVA suggérée avec la RG
    const vatAmountWithRg = Math.round(htAfterRg * (ratePercent / 100) * 100) / 100;

    // Afficher la suggestion
    suggestionText.innerHTML = `
      Base HT calculée : <strong>${this.formatCurrency(baseHtFromVat)}</strong><br>
      Après RG 5% : <strong>${this.formatCurrency(htAfterRg)}</strong><br>
      TVA ${ratePercent}% suggérée : <strong>${this.formatCurrency(vatAmountWithRg)}</strong>
    `;
    suggestionDiv.classList.remove('hidden');
  }

  hideSuggestions() {
    // Masquer toutes les suggestions
    if (this.hasSuggestion55Target) this.suggestion55Target.classList.add('hidden');
    if (this.hasSuggestion10Target) this.suggestion10Target.classList.add('hidden');
    if (this.hasSuggestion20Target) this.suggestion20Target.classList.add('hidden');

    // Masquer le panneau d'aide
    if (this.hasHelpPanelTarget) {
      this.helpPanelTarget.classList.add('hidden');
    }
  }

  calculateTotal() {
    // Calculer la somme des montants de TVA saisis
    let total = 0;
    this.vatFieldTargets.forEach((field) => {
      const input = field.querySelector('input');
      if (input) {
        // Nettoyer et convertir la valeur (gérer virgules et espaces)
        const value = this.parseAmount(input.value);
        total += value;
      }
    });

    // Afficher le total formaté (deux fois pour les deux affichages)
    this.totalDisplayTarget.textContent = this.formatCurrency(total);
    if (this.hasTotalDisplay2Target) {
      this.totalDisplay2Target.textContent = this.formatCurrency(total).replace(' €', '');
    }

    // Récupérer le montant HT — soit depuis l'input legacy, soit depuis le total
    // émis via `invoice-lines:total-changed` (multi-invoice mode).
    const htValue = this.#currentHtValue();

    // Afficher le montant HT
    if (this.hasHtDisplayTarget) {
      this.htDisplayTarget.textContent = this.formatCurrency(htValue).replace(' €', '');
    }

    // Calculer et afficher le TTC
    const ttcValue = htValue + total;
    this.ttcAmountTarget.textContent = this.formatCurrency(ttcValue);

    // Valider le montant de TVA par rapport au HT
    this.validateVatAmount(total, htValue);

    // Recalculer les suggestions si le montant HT change
    this.calculateSuggestions();
  }

  // Listener for `invoice-lines:total-changed@window` — caches the new total
  // and triggers a TTC recompute so the summary card stays in sync. The
  // Phase 6 refactor removed the DERIVED-mode guard: this controller only
  // mounts on the no-invoices path, where the TTC display targets always
  // exist on the legacy block.
  onTotalChanged({ detail: { totalHt } = {} } = {}) {
    this._invoiceLinesTotalHt = parseFloat(totalHt) || 0;
    this.calculateTotal();
  }

  // Source of truth for the HT amount, with fallback chain:
  //   1. Legacy htInput (single-invoice / no-lines mode)
  //   2. Cached value from invoice-lines:total-changed (multi-invoice mode)
  //   3. 0
  #currentHtValue() {
    if (this.hasHtInputTarget) return this.parseAmount(this.htInputTarget.value);
    if (typeof this._invoiceLinesTotalHt === 'number') return this._invoiceLinesTotalHt;
    return 0;
  }

  validateVatAmount(totalVat, htAmount) {
    // Si pas de montant HT, ne pas valider
    if (htAmount <= 0) {
      if (this.hasWarningBoxTarget) {
        this.warningBoxTarget.classList.add('hidden');
      }
      return;
    }

    // Calculer le pourcentage de TVA par rapport au HT
    const vatPercentage = (totalVat / htAmount) * 100;

    // Définir les seuils de validation (avec une marge pour les arrondis)
    const MIN_VAT_PERCENTAGE = 4; // En-dessous de 5,5% (taux minimum en France)
    const MAX_VAT_PERCENTAGE = 22; // Au-dessus de 20% (taux maximum en France)

    // Afficher un avertissement si hors des limites
    if (this.hasWarningBoxTarget && this.hasWarningMessageTarget) {
      if (
        vatPercentage > MAX_VAT_PERCENTAGE ||
        (totalVat > 0 && vatPercentage < MIN_VAT_PERCENTAGE)
      ) {
        // Construire le message d'avertissement
        let message = `Le total de TVA (${this.formatCurrency(totalVat)}) représente ${vatPercentage.toFixed(1)}% du montant HT`;

        if (vatPercentage > MAX_VAT_PERCENTAGE) {
          message += `, ce qui est supérieur au taux maximum de 20%.`;
        } else {
          message += `, ce qui est inférieur au taux minimum de 5,5%.`;
        }

        message += ` Veuillez vérifier vos saisies.`;

        this.warningMessageTarget.textContent = message;
        this.warningBoxTarget.classList.remove('hidden');
      } else {
        // Masquer l'avertissement si tout est OK
        this.warningBoxTarget.classList.add('hidden');
      }
    }
  }

  // Parse un montant en gérant les formats français (virgule, espaces)
  parseAmount(value) {
    if (!value) return 0;
    // Nettoyer : supprimer espaces, symboles €, puis remplacer virgule par point
    const cleaned = value.toString().replace(/\s/g, '').replace(/€/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  // Formate un nombre en devise française
  formatCurrency(value) {
    return (
      new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' €'
    );
  }
}
